import React, { useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import AdminPageShell from '../../components/admin/AdminPageShell';
import AdminPagination, {
  AdminSearchRow,
} from '../../components/admin/AdminPagination';
import { ConfirmModal } from '../../components/dicionarios';
import useAdminList from '../../hooks/useAdminList';
import * as adminFuncionarios from '../../services/adminFuncionarios';
import handleFieldChange from '../../components/admin/formHelpers';

const UNIDADES = ['ADM', 'RECEPCA', 'ESTUDOS', 'EXAMES', 'MEDICO', 'NUTRI'];
const NIVELS = ['TOTAL', 'TOTAL2', 'PARCIAL1', 'PARCIAL2', 'MINIMO'];

const emptyForm = {
  codFunc: '',
  nomeFunc: '',
  unidade: 'ADM',
  ativo: 'S',
  prontuario: 'N',
  usuario: '',
  nivelAcesso: 'PARCIAL1',
  webAcesso: 'PARCIAL1',
  senha: '',
  cpf: '',
  celular: '',
};

export default function Funcionarios() {
  const [filters, setFilters] = useState({ unidade: '', ativo: '' });
  const list = useAdminList(
    'admin-funcionarios',
    adminFuncionarios.listFuncionarios,
    filters
  );
  const [formData, setFormData] = useState(emptyForm);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const selectItem = index => {
    const item = list.items[index];
    if (!item) return;
    setSelectedIndex(index);
    setFormData({
      codFunc: item.codFunc ?? '',
      nomeFunc: item.nomeFunc ?? '',
      unidade: item.unidade ?? '',
      ativo: item.ativo ?? 'S',
      prontuario: item.prontuario ?? '',
      usuario: item.usuario ?? '',
      nivelAcesso: item.nivelAcesso ?? '',
      webAcesso: item.webAcesso ?? '',
      senha: '',
      cpf: item.cpf ?? '',
      celular: item.celular ?? '',
    });
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleNew = () => {
    setFormData(emptyForm);
    setIsCreating(true);
    setIsEditing(true);
  };

  const handleEdit = () => {
    if (!list.items.length) {
      toast.warn('Nenhum registro para editar!');
      return;
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    if (list.items.length) selectItem(Math.min(selectedIndex, list.items.length - 1));
    else setFormData(emptyForm);
  };

  const buildPayload = () => {
    const payload = {
      nomeFunc: formData.nomeFunc.trim(),
      unidade: formData.unidade || null,
      ativo: formData.ativo || 'S',
      prontuario: formData.prontuario || null,
      usuario: formData.usuario || null,
      nivelAcesso: formData.nivelAcesso || null,
      webAcesso: formData.webAcesso || null,
      cpf: formData.cpf || null,
      celular: formData.celular || null,
    };
    if (formData.senha) payload.senha = formData.senha;
    if (isCreating) payload.codFunc = formData.codFunc.trim();
    return payload;
  };

  const handleSave = async () => {
    if (!formData.nomeFunc?.trim()) {
      toast.warn('Nome é obrigatório!');
      return;
    }
    if (isCreating && !formData.codFunc?.trim()) {
      toast.warn('Código do funcionário é obrigatório!');
      return;
    }
    if (isCreating && !formData.senha) {
      toast.warn('Senha é obrigatória no cadastro!');
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildPayload();
      if (isCreating) {
        await adminFuncionarios.createFuncionario(payload);
        toast.success('Usuário criado!');
      } else {
        await adminFuncionarios.updateFuncionario(formData.codFunc, payload);
        toast.success('Usuário atualizado!');
      }
      setIsCreating(false);
      setIsEditing(false);
      setFormData(prev => ({ ...prev, senha: '' }));
      await list.mutate();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao gravar usuário!');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await adminFuncionarios.deleteFuncionario(formData.codFunc);
      toast.success('Usuário desativado!');
      setShowDeleteConfirm(false);
      await list.mutate();
      setFormData(emptyForm);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao desativar usuário!');
    }
  };

  return (
    <AdminPageShell
      title="Cadastro de Usuários"
      subtitle="Funcionários e níveis de acesso"
    >
      <AdminSearchRow q={list.q} onChange={list.setQ} placeholder="Buscar nome, usuário ou código">
        <Row>
          <Col xs={6}>
            <Form.Control
              as="select"
              value={filters.unidade}
              onChange={handleFieldChange(setFilters, 'unidade')}
              aria-label="Filtrar unidade"
            >
              <option value="">Todas unidades</option>
              {UNIDADES.map(u => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </Form.Control>
          </Col>
          <Col xs={6}>
            <Form.Control
              as="select"
              value={filters.ativo}
              onChange={handleFieldChange(setFilters, 'ativo')}
              aria-label="Filtrar ativo"
            >
              <option value="">Ativo e inativo</option>
              <option value="S">Ativo</option>
              <option value="N">Inativo</option>
            </Form.Control>
          </Col>
        </Row>
      </AdminSearchRow>

      {list.isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table bordered hover size="sm">
              <thead>
                <tr>
                  <th>Cód.</th>
                  <th>Nome</th>
                  <th>Unidade</th>
                  <th>Ativo</th>
                  <th>Usuário</th>
                  <th>Web Acesso</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item, index) => (
                  <tr
                    key={item.codFunc}
                    onClick={() => selectItem(index)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor:
                        index === selectedIndex && !isCreating
                          ? '#e2f0f0'
                          : undefined,
                    }}
                  >
                    <td>{item.codFunc}</td>
                    <td>{item.nomeFunc}</td>
                    <td>{item.unidade}</td>
                    <td>{item.ativo}</td>
                    <td>{item.usuario}</td>
                    <td>{item.webAcesso}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {!list.items.length && (
            <Alert variant="light" className="text-center">
              Nenhum usuário encontrado.
            </Alert>
          )}
          <AdminPagination
            page={list.page}
            total={list.total}
            limit={list.limit}
            onPageChange={list.setPage}
          />
        </>
      )}

      <hr />
      <Form>
        <Row>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Cód. Func.</Form.Label>
              <Form.Control
                value={formData.codFunc}
                disabled={!isCreating}
                onChange={handleFieldChange(setFormData, 'codFunc')}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Nome completo</Form.Label>
              <Form.Control
                value={formData.nomeFunc}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'nomeFunc')}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Unidade</Form.Label>
              <Form.Control
                as="select"
                value={formData.unidade}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'unidade')}
              >
                {UNIDADES.map(u => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Ativo</Form.Label>
              <Form.Control
                as="select"
                value={formData.ativo}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'ativo')}
              >
                <option value="S">S</option>
                <option value="N">N</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Prontuário</Form.Label>
              <Form.Control
                as="select"
                value={formData.prontuario}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'prontuario')}
              >
                <option value="S">S</option>
                <option value="N">N</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Usuário (login)</Form.Label>
              <Form.Control
                value={formData.usuario}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'usuario')}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Nível acesso</Form.Label>
              <Form.Control
                as="select"
                value={formData.nivelAcesso}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'nivelAcesso')}
              >
                {NIVELS.map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Web acesso</Form.Label>
              <Form.Control
                as="select"
                value={formData.webAcesso}
                disabled={!isEditing}
                onChange={handleFieldChange(setFormData, 'webAcesso')}
              >
                {[...NIVELS, 'ADM'].map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Senha</Form.Label>
              <Form.Control
                type="password"
                value={formData.senha}
                disabled={!isEditing}
                placeholder={isCreating ? 'Obrigatória' : 'Deixe vazio p/ manter'}
                onChange={handleFieldChange(setFormData, 'senha')}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      <div className="d-flex flex-wrap mt-3">
        <Button variant="primary" className="mr-2 mb-2" onClick={handleNew} disabled={isEditing}>
          Novo
        </Button>
        <Button variant="secondary" className="mr-2 mb-2" onClick={handleEdit} disabled={isEditing || !list.items.length}>
          Editar
        </Button>
        <Button variant="success" className="mr-2 mb-2" onClick={handleSave} disabled={!isEditing || isSaving}>
          {isSaving ? 'Gravando...' : 'Gravar'}
        </Button>
        <Button variant="warning" className="mr-2 mb-2" onClick={handleCancel} disabled={!isEditing}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          className="mb-2"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isCreating || !formData.codFunc || isEditing}
        >
          Desativar
        </Button>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        title="Desativar usuário"
        message="Confirma desativar este funcionário?"
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
      />
    </AdminPageShell>
  );
}
